import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import logo from "images/logo-new.svg";
import api from "constants/api";

interface CartItem {
    product_id: number;
    code: string;
    name: string;
    price: number;
    weight: number;
    quantity: number;
    GST: number;
    discount?: number;
    no_lead_day?: number;
    project_no?: string;
    HSN_code?: string;
    delivery_status?: string;
    invoice_number?: string;
    invoice_date?: string;
    tracking_reference?: string;
    carrier?: string;
}

interface Enquiry {
    _id: string;
    order_number: string;
    odoo_display_name?: string;
    name: string;
    email: string;
    mobile: string;
    company: string;
    address: string;
    state: string;
    country: string;
    pincode: string;
    delivery_company: string;
    delivery_address: string;
    delivery_state: string;
    delivery_country: string;
    delivery_pincode: string;
    delivery_charges: number;
    payment_terms: string;
    createdAt: string;
    cart_items: CartItem[];
}

const OrderView: React.FC = () => {
    const { id } = useParams();
    const location = useLocation();
    const [enquiry, setEnquiry] = useState<Enquiry | null>(location.state?.enquiry || null);
    const [loading, setLoading] = useState(!enquiry);
    const [error, setError] = useState<string | null>(null);
    const [show, setShow] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

    const handleMouseEnter = (
        e: React.MouseEvent<SVGSVGElement>,
        idx: number
    ) => {
        const rect = e.currentTarget.getBoundingClientRect();

        setTooltipPos({
            top: rect.top + window.scrollY,
            left: rect.right + 12,
        });

        setShow(idx);
    };

    const handleMouseLeave = () => {
        setShow(null);
    };


    useEffect(() => {
        // Fetch from API if no enquiry in location.state
        if (!enquiry && id) {
            const fetchEnquiry = async () => {
                setLoading(true);
                setError(null);
                try {
                    const token = JSON.parse(localStorage.getItem("AuthCustomer") || "{}");
                    const response = await axios.post(
                        `${process.env.REACT_APP_API_URL}${api.inquiryDetail}`,
                        { id },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    if (response.data?.statusCode === 200) {
                        setEnquiry(response.data.data.enquiry);
                    } else {
                        setError("Enquiry not found.");
                    }
                } catch (err) {
                    console.error(err);
                    setError("Failed to fetch enquiry.");
                } finally {
                    setLoading(false);
                }
            };
            fetchEnquiry();
        }
    }, [id, enquiry]);

    if (loading) return <p className="text-center mt-10">Loading enquiry details...</p>;
    if (error || !enquiry) return <p className="text-center mt-10 text-red-500">{error || "No enquiry data found."}</p>;

    // Calculate totals
    // Calculate totals
    const parseGST = (gstString: any) => {
        if (!gstString) return 0;
        const match = gstString.toString().match(/(\d+(\.\d+)?)/);
        return match ? parseFloat(match[1]) : 0;
    };

    const untaxedAmount = enquiry.cart_items.reduce(
        (acc: number, item: any) => acc + item.price * (item.quantity || 1),
        0
    );

    const discountAmount = enquiry.cart_items.reduce((acc: number, item: any) => {
        const qty = item.quantity || 1;
        const itemSubtotal = item.price * qty;
        const itemDiscount = (itemSubtotal * (item.discount || 0)) / 100;
        return acc + itemDiscount;
    }, 0);

    const totalAfterDiscount = untaxedAmount - discountAmount;

    // product GST calculation
    const productGST = enquiry.cart_items.reduce((acc: number, item: any) => {
        const qty = item.quantity || 1;
        const itemSubtotal = item.price * qty;
        const itemDiscount = (itemSubtotal * (item.discount || 0)) / 100;
        const taxableAmount = itemSubtotal - itemDiscount;

        const gstPercentage = parseGST(item.GST);
        return acc + (taxableAmount * gstPercentage) / 100;
    }, 0);

    // GST on delivery charges (always 18%)
    const deliveryCharges = enquiry.delivery_charges || 0;
    const deliveryGST = deliveryCharges * 0.18;

    // 🔥 Combine both GST values into `taxedAmount`
    const taxedAmount = productGST + deliveryGST;

    const total = totalAfterDiscount + taxedAmount + deliveryCharges;
    const roundedTotal = Math.round(total);
    const roundOffValue = (roundedTotal - total).toFixed(2);

    return (
        <div className="bg-white py-4 sm:py-6 md:py-8">
            <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-12">
                {/* Header */}
                <div className="grid grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-0 items-center">
                    {/* <img src={logo} alt="logo" className="w-32 sm:w-40 md:w-64" /> */}
                    <div className="text-left sm:text-left ">
                        <h1 className="text-lg sm:text-xl md:text-2xl font-semibold">{enquiry.odoo_display_name}</h1>
                    </div>
                    <div className="flex justify-end ">
                        <button
                            onClick={() => window.location.href = "/orderlist"}
                            className="px-4 py-2 bg-slate-900 text-white rounded-xl font-medium  focus:outline-none focus:bg-slate-900"
                        >
                            Back
                        </button>
                    </div>
                </div>

                {/* Customer & Shipping Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                    <div>
                        <h2 className="font-medium">Invoice Address</h2>
                        <p>
                            {enquiry.name}<br />
                            {enquiry.company}<br />
                            {enquiry.address}<br />
                            {enquiry.state} {enquiry.country} {enquiry.pincode}<br />
                            Mo: {enquiry.mobile}<br />
                            Email: {enquiry.email}
                        </p>
                    </div>
                    <div className="text-left md:text-right">
                        <h2 className="font-medium">Shipping Address</h2>
                        <p>
                            {enquiry.name}<br />
                            {enquiry.delivery_company}<br />
                            {enquiry.delivery_address}<br />
                            {enquiry.delivery_state} {enquiry.delivery_country} {enquiry.delivery_pincode}
                        </p>
                        <p>Payment Terms: {enquiry.payment_terms}</p>
                    </div>
                </div>

                {/* Order Table */}
                <div className="relative">
                    <div className="mt-6 overflow-x-auto">
                        <table className="min-w-full border-collapse">
                            <thead>
                                <tr className="bg-[#5e4666] text-white">
                                    <th className="p-2 border">SR.<br />NO.</th>
                                    <th className="p-2 border">Description</th>
                                    <th className="p-2 border">HSN/SAC</th>
                                    <th className="p-2 border">Lead Time (Days)</th>
                                    <th className="p-2 border">Qty (UNITS)</th>
                                    <th className="p-2 border">UNIT PRICE</th>
                                    <th className="p-2 border">DISC.%</th>
                                    <th className="p-2 border">TAXES</th>
                                    <th className="p-2 border">AMOUNT</th>
                                    <th className="relative p-2 border w-[100px]">DELIVERY STATUS </th>
                                </tr>
                            </thead>
                            <tbody>
                                {enquiry.cart_items.map((item, idx) => {
                                    const gstPercent = parseGST(item.GST);
                                    const itemDiscount = (item.price * item.quantity * (item.discount || 0)) / 100;
                                    const itemTaxable = item.price * item.quantity - itemDiscount;
                                    const itemGSTValue = (itemTaxable * gstPercent) / 100;
                                    const itemAmount = itemTaxable + itemGSTValue; //
                                    return (
                                        <tr key={idx} className="relative">
                                            <td className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black text-sm sm:text-base">{idx + 1}</td>
                                            <td className="border border-gray-300 p-2 sm:p-3 md:p-4 text-left font-medium text-black text-sm sm:text-base">{item.name} <br />{item.project_no}</td>
                                            <td className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black text-sm sm:text-base">{item.HSN_code}</td>
                                            <td className="p-2 border border-gray-300 text-center">{item.no_lead_day}</td>
                                            <td className="p-2 border border-gray-300 text-center">{item.quantity}</td>
                                            <td className="p-2 border border-gray-300 text-center">{item.price.toFixed(2)}</td>
                                            <td className="p-2 border border-gray-300 text-center">{item.discount || 0}</td>
                                            <td className="p-2 border border-gray-300 text-center relative">
                                                {(() => {
                                                    if (!item.GST) return "0%";
                                                    const match = String(item.GST).match(/(\d+(\.\d+)?)%/);
                                                    return match ? `${match[1]}%` : "0%";
                                                })()}
                                            </td>
                                            <td className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#f3f4f6] text-sm sm:text-base">{itemAmount.toFixed(2)}</td>
                                            <td className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#f3f4f6] text-sm sm:text-base relative">{item.delivery_status} <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5 inline-block ml-0.5"
                                                onMouseEnter={(e) => handleMouseEnter(e, idx)}
                                                onMouseLeave={handleMouseLeave}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                                            </svg>
                                            </td>
                                            {show === idx && (
                                                <div className="absolute z-[9999]
                                                    w-auto bg-white rounded-lg shadow-xl
                                                    border border-gray-400 p-4 text-sm text-left"
                                                    style={{
                                                        bottom: "10px",
                                                        right: "100px"
                                                    }}>
                                                    <h4 className="font-normal text-gray-900 mb-1">
                                                        Invoice No:<span className="font-semibold ml-1">{item.invoice_number}</span>
                                                    </h4>
                                                    <h6 className="font-normal text-gray-900 mb-1">
                                                        Invoice Date:
                                                        <span className="font-semibold ml-1">
                                                            {item.invoice_date
                                                                ? item.invoice_date.split("-").reverse().join("-")
                                                                : ""}
                                                        </span>
                                                    </h6>
                                                    <h5 className="font-normal text-gray-900 mb-1">
                                                        Courier/Transporter Name:<span className="font-semibold ml-1">{Array.isArray(item.carrier) ? item.carrier[1] : item.carrier}</span>
                                                    </h5>
                                                    <h4 className="font-normal text-gray-900 mb-1">
                                                        Tracking No:<span className="font-semibold ml-1">{item.tracking_reference}</span>
                                                    </h4>
                                                </div>
                                            )}
                                        </tr>
                                    );
                                })}

                            </tbody>
                            <tfoot >
                                <tr>
                                    <td colSpan={5}></td>
                                    <td colSpan={3} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-white text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">Untaxed Amount </p>
                                    </td>
                                    <td colSpan={1} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-white text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">₹{untaxedAmount.toFixed(2)}</p>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5}></td>
                                    <td colSpan={3} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#f3f4f6] text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">Discount</p>
                                    </td>
                                    <td colSpan={1} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#f3f4f6] text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">₹{discountAmount.toFixed(2)}</p>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5}></td>
                                    <td colSpan={3} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-white text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">Freight Charges</p>
                                    </td>
                                    <td colSpan={1} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-white text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">₹{enquiry.delivery_charges.toFixed(2)}</p>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5}></td>
                                    <td colSpan={3} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-white text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">IGST</p>
                                    </td>
                                    <td colSpan={1} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-white text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">₹{taxedAmount.toFixed(2)}</p>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5}></td>
                                    <td colSpan={3} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#f3f4f6] text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">Round Off. Amount</p>
                                    </td>
                                    <td colSpan={1} className="border border-gray-300 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#f3f4f6] text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-black">₹{roundOffValue}</p>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5}></td>
                                    <td colSpan={3} className="rounded-bl-[20px] border-0 border-r p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#5e4666] text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-white">Total Amount</p>
                                    </td>
                                    <td colSpan={1} className="rounded-br-[20px] border-0 p-2 sm:p-3 md:p-4 text-center font-medium text-black bg-[#5e4666] text-sm sm:text-base">
                                        <p className="text-sm sm:text-base font-medium text-white">₹{roundedTotal.toFixed(2)}</p>
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderView;
